'use client';

import React, { FC } from 'react';
import styles from './ProductPage.module.scss';
import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from '@/src/components/ui/label/Label';
import { ITheme } from '@/src/lib/types/themeInterface';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TPage = {
  theme: ITheme;
};

const heading = 'Product Page Optimized to Sell';
const heading2 = 'Quick Preview, Smooth Shopping';
const excerpt =
  'Increase purchases using optimized product pages that guide customers with engaging content and smooth checkout.';

type MainProductProps = {
  image: string;
  title: string;
  text: string;
  actions?: React.ReactNode;
};

type GridItemProps = {
  image?: string;
  video?: string;
  title: string;
  desc: string;
};

type ListBlockProps = {
  image: string;
  label: string;
  text: string;
};

const mainProduct: MainProductProps = {
  image: '/images/themes/stockist/product/main.webp',
  title: 'Create Unique Product Pages',
  text: 'Empower your store with customizable product pages that highlight your products’ best features and turn browsers into loyal customers.',
};

const gridItems: GridItemProps[] = [
  {
    image: '/images/themes/stockist/product/item-1.webp',
    title: 'Unique Modals',
    desc: 'Showcase product details, shipping info, variants, and sizes using tailored modals—no page reloads needed.',
  },
  {
    image: '/images/themes/stockist/product/item-2.webp',
    title: 'Galleries',
    desc: 'Show products with clean grid layouts and smooth sliders for a complete visual experience.',
  },
  {
    image: '/images/themes/stockist/product/item-3.webp',
    title: 'Variant Picker',
    desc: 'Present product options with swatches, pills, or image thumbnails in a clear easy-to-use way—making selection simple.',
  },
];

const listBlocks: ListBlockProps[] = [
  {
    image: '/images/themes/stockist/product/card-1.webp',
    label: 'Cross-Sell',
    text: 'Suggest matching products to grow your average cart size.',
  },
  {
    image: '/images/themes/stockist/product/card-2.webp',
    label: 'Stock Counter',
    text: 'Show limited stock to drive urgency and encourage quick action.',
  },
  {
    image: '/images/themes/stockist/product/card-3.webp',
    label: 'Quick Add Modal',
    text: 'Speed up purchases with a simple, on-page add-to-cart popup.',
  },
  {
    image: '/images/themes/stockist/product/card-4.webp',
    label: 'Product Description',
    text: 'Highlight product features and benefits in a clear, scannable format.',
  },
];

const ProductPage: FC<TPage> = ({ theme }) => {
  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  const { ref: inViewRef3, inView: inView3 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  return (
    <section className={styles.productPage}>
      <Wrapper>
        <div className={styles.productPage__head} ref={inViewRef2}>
          <Label>Product page</Label>
          <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
            {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
          </h2>
          <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
            {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
          </p>
        </div>

        <div className={styles['productPage__main']}>
          <div className={styles['productPage__main-pic']}>
            <NextImage
              src={mainProduct.image}
              alt={mainProduct.title}
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 1280px) 60vw, 80vw"
              loading="lazy"
            />
          </div>
          <div className={styles['productPage__main-about']}>
            <h2 className={styles['productPage__main-title']}>{mainProduct.title}</h2>
            <div className={styles['productPage__main-text']}>{mainProduct.text}</div>
            <div className={styles['productPage__main-actions']}>
              <ShopifyStoreButton isWhite={false} theme={theme} />
            </div>
          </div>
        </div>

        <div className={styles['productPage__banner']}>
          <div className={styles['productPage__banner-about']} ref={inViewRef3}>
            <h2 className={`heading__2 ${styles.heading} ${inView3 && styles.animate__heading}`}>
              {createAnimationArr(heading2, styles.word__wrapper, 0, 0.01)}
            </h2>

            <div className={styles['productPage__banner-desc']}>
              Let customers instantly preview products without leaving the page, making shopping
              faster, easier, and more enjoyable.
            </div>
          </div>

          <div className={styles['productPage__banner-pic']}>
            <NextImage
              src="/images/themes/stockist/product/banner-mobile.webp"
              alt="BannerMobile"
              fill
              sizes="100vw"
              loading="lazy"
            />
            <NextImage
              src="/images/themes/stockist/product/banner-tablet.webp"
              alt="BannerTablet"
              fill
              sizes="100vw"
              loading="lazy"
            />
            <NextImage
              src="/images/themes/stockist/product/banner.webp"
              alt="BannerDesktop"
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 1280px) 60vw, 70vw"
              loading="lazy"
            />
          </div>
        </div>

        <div className={styles['productPage__grid']}>
          {gridItems.map((item, idx) => (
            <div key={idx} className={styles['productPage__item']}>
              <div className={styles['productPage__pic']}>
                {item.video ? (
                  <video src={item.video} autoPlay playsInline muted loop />
                ) : item.image ? (
                  <NextImage
                    src={item.image}
                    alt={item.desc}
                    fill
                    sizes="(max-width: 480px) 100vw, 33vw"
                    loading="lazy"
                  />
                ) : null}
              </div>
              <div className={styles['productPage__about']}>
                <h3 className={styles['productPage__title']}>{item.title}</h3>
                <div className={styles['productPage__desc']}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles['productPage__list']}>
          {listBlocks.map((block, idx) => (
            <div key={idx} className={styles['productPage__block']}>
              <div className={styles['productPage__img']}>
                <NextImage
                  src={block.image}
                  alt={block.label}
                  fill
                  sizes="(max-width: 480px) 100vw, 33vw"
                  loading="lazy"
                />
              </div>
              <h4 className={styles['productPage__label']}>{block.label}</h4>
              <div className={styles['productPage__text']}>{block.text}</div>
            </div>
          ))}
        </div>
      </Wrapper>
    </section>
  );
};

export default ProductPage;
